import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { parentService, ParentNotification, ParentPortalResponse } from '@/services/parent.service';

interface ParentState {
  dashboard: ParentPortalResponse | null;
  notifications: ParentNotification[];
  unreadNotificationCount: number;
  selectedChildId: string | null;
  loading: {
    dashboard: boolean;
    notifications: boolean;
  };
  error: {
    dashboard: string | null;
    notifications: string | null;
  };
}

const initialState: ParentState = {
  dashboard: null,
  notifications: [],
  unreadNotificationCount: 0,
  selectedChildId: null,
  loading: {
    dashboard: false,
    notifications: false,
  },
  error: {
    dashboard: null,
    notifications: null,
  },
};

export const fetchParentDashboard = createAsyncThunk(
  'parent/fetchDashboard',
  async () => {
    return await parentService.getParentPortal();
  }
);

export const fetchNotifications = createAsyncThunk(
  'parent/fetchNotifications',
  async (params?: { unread_only?: boolean }) => {
    return await parentService.getNotifications(params);
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'parent/markNotificationRead',
  async (notificationId: string) => {
    await parentService.markNotificationAsRead(notificationId);
    return notificationId;
  }
);

const parentSlice = createSlice({
  name: 'parent',
  initialState,
  reducers: {
    setSelectedChild: (state, action: PayloadAction<string>) => {
      state.selectedChildId = action.payload;
    },
    clearSelectedChild: (state) => {
      state.selectedChildId = null;
    },
    addNotification: (state, action: PayloadAction<ParentNotification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.is_read) {
        state.unreadNotificationCount++;
      }
    },
    addMessage: (state, _action: PayloadAction<any>) => {
      // Placeholder for WebSocket message integration
    },
    clearErrors: (state) => {
      state.error = { dashboard: null, notifications: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchParentDashboard.pending, (state) => {
        state.loading.dashboard = true;
        state.error.dashboard = null;
      })
      .addCase(fetchParentDashboard.fulfilled, (state, action) => {
        state.loading.dashboard = false;
        state.dashboard = action.payload;
        state.unreadNotificationCount = action.payload.unread_notifications;
      })
      .addCase(fetchParentDashboard.rejected, (state, action) => {
        state.loading.dashboard = false;
        state.error.dashboard = action.error.message || 'Failed to fetch dashboard';
      });

    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading.notifications = true;
        state.error.notifications = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading.notifications = false;
        state.notifications = action.payload;
        state.unreadNotificationCount = action.payload.filter(n => !n.is_read).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading.notifications = false;
        state.error.notifications = action.error.message || 'Failed to fetch notifications';
      });

    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.is_read) {
          notification.is_read = true;
          state.unreadNotificationCount = Math.max(0, state.unreadNotificationCount - 1);
        }
      });
  },
});

export const {
  setSelectedChild,
  clearSelectedChild,
  addNotification,
  addMessage,
  clearErrors,
} = parentSlice.actions;

export default parentSlice.reducer;